import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * Bouton shadcn/ui ajusté au système Majuscule (components/Button) :
 * hauteurs control-sm 28 / md 32 / lg 36, rayon md, anneau de focus `ring`
 * 2px décalé de 2px, icône lucide 16px à gauche du libellé.
 */
const buttonVariants = cva(
  [
    'inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-transparent',
    'text-sm font-medium transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'disabled:cursor-not-allowed disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:opacity-50',
    '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  ].join(' '),
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:opacity-90',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-accent hover:text-accent-foreground',
        outline:
          'border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground aria-[current=page]:border-foreground aria-[current=page]:bg-accent',
        ghost: 'text-foreground hover:bg-accent hover:text-accent-foreground',
        destructive: 'bg-destructive text-destructive-foreground hover:opacity-90',
        /** Action destructive secondaire dans une ligne de tableau (contour, texte rouge). */
        'outline-destructive':
          'border-input bg-background text-danger-fg hover:bg-accent hover:text-danger-fg',
        link: 'h-auto border-0 px-0 text-foreground underline underline-offset-[3px] hover:opacity-80',
      },
      size: {
        default: 'h-8 px-3',
        xs: 'h-6 px-2 text-xs',
        sm: 'h-7 px-2 text-[13px]',
        lg: 'h-9 px-4',
        icon: 'h-8 w-8 px-0',
        'icon-sm': 'h-7 w-7 px-0',
        /** Bouton de numéro de page (`btn-page`) : 28px, chiffres mono. */
        page: 'h-7 w-7 px-0 font-mono text-xs',
      },
    },
    compoundVariants: [{ variant: 'link', className: 'h-auto px-0' }],
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    // Comme le <button> natif (et shadcn) : sans `type`, un bouton dans un formulaire le soumet.
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
